import React, { useState, useCallback, useEffect, useRef } from "react";
import PropTypes from "prop-types";
import { Link } from "react-router";
import { OFFICER_STATUS_TO_FETCH_DATA } from "@constants";
import { OfficerService, OrganizationFavoriteGroupsService } from "@services";
import utils from "@utils";
import { ConfirmationModal, Image, ScrollDetect, ReactSelect, Switch } from "../../../components";
import { RemoveGroupModal, AddGroupModal, OfficerGroupsModal } from "./components";

import "./OfficerGroups.scss";

const OfficerGroups = ({ user }) => {
	const { lang } = global;
	const queryParams = {
		displayCount: 25,
		dateRange: 7,
		sortedColumn: "name",
		sortBy: "Name",
		asc: true,
	};

	const _addGroupModal = useRef(null);
	const _removeGroupModal = useRef(null);
	const _officerGroupsModal = useRef(null);
	const _confirmationModal = useRef(null);

	const [officers, setOfficers] = useState([]);
	const [officersCount, setOfficersCount] = useState(0);
	const [pageNumber, setPageNumber] = useState(1);
	const [visible, setVisible] = useState(false);
	const [hasMore, setHasMore] = useState(true);
	const [showMyOfficers, setShowMyOfficers] = useState(user.isLEA());

	const [rawGroups, setRawGroups] = useState([]);
	const [groupOptions, setGroupOptions] = useState([]);
	const [loading, setLoading] = useState(false);
	const [loadingGroups, setLoadingGroups] = useState(true);

	const [favoriteGroup, setFavoriteGroup] = useState({
		Id: "",
		Name: "",
	});

	const [search, setSearch] = useState({
		activeSearch: false,
		searchQuery: "",
	});

	/** Gets a list of grouped officers */
	const getGroupedOfficers = useCallback(() => {
		const requestParams = {
			...queryParams,
			pageNumber,
			vettedStatus: OFFICER_STATUS_TO_FETCH_DATA,
			jurisdictionOfficers: showMyOfficers,
		};
		if (favoriteGroup.Id !== "") {
			requestParams.favoriteGroupId = favoriteGroup.Id;
			requestParams.favoriteGroupName = favoriteGroup.Name;
		}
		OfficerService.getAll(requestParams)
			.then(({ data }) => {
				const newData = data?.ItemList ?? [];
				const newOfficers = newData.map(officer => ({
					id: officer.Id,
					image: officer.ContactPhotoUrl,
					name: `${officer.FirstName} ${officer.LastName}`,
					jurisdiction: officer.Department,
					jurisdictionId: officer.JurisdictionId,
					rank: officer.OfficerRank,
					experience: officer.JobCount,
					email: officer.EmailAddress,
					grouped: officer.IsFavorite,
				}));
				// Receive data
				setOfficers(pageNumber > 1 ? [...officers, ...newOfficers] : newOfficers);
				setOfficersCount(data.Count);
				setPageNumber(pageNumber + 1);
				setHasMore(newOfficers.length >= queryParams.displayCount);
			})
			.catch(error => {
				setOfficers([]);
				setOfficersCount(0);
				setPageNumber(1);
				setHasMore(false);
				utils.notify({
					type: "error",
					message:
						error.response.data.Message || lang.t("Error loading Grouped Officers"),
				});
			});
	});

	/**
	 * Gets a list of available groups
	 * @param {Function} callback A callback method to handle any logic necessary for adding an officer to a group
	 */
	const getGroups = (callback = null) => {
		const favoriteId = favoriteGroup.Id;
		OrganizationFavoriteGroupsService.getGroups()
			.then(({ data }) => {
				const options = data.reduce(
					(groupsList, group) => [
						...groupsList,
						{ label: group.Name, value: group.Id, owned: group.Owned },
					],
					[{ label: "All Groups", value: "", owned: true }],
				);

				const nonAdminOptions = options.filter(item => item.owned === true);

				const newFavoriteGroup = {
					Id: "",
					Name: "",
				};

				if (favoriteGroup) {
					const selectedFavGroup = data.find(group => group.Id === favoriteId);
					newFavoriteGroup.Id = selectedFavGroup ? selectedFavGroup.Id : "";
					newFavoriteGroup.Name = selectedFavGroup ? selectedFavGroup.Name : "";
				}

				setGroupOptions(user.IsStaffAdmin ? options : nonAdminOptions);
				setRawGroups(data);
				setFavoriteGroup(newFavoriteGroup);

				if (callback) callback();
			})
			.catch(({ response }) => {
				utils.notify({
					type: "error",
					message: response.data.Message || lang.t("Error loading Groups"),
				});
			})
			.finally(() => setLoadingGroups(false));
	};

	/**
	 * Gets a list of available groups tha an officer is part of
	 * @param {Number} officerId The unique id of the selected officer
	 * @param {Function} callback A callback method to handle any logic necessary for opening the officer groups moda;
	 */
	const getOfficerGroups = (officerId, callback) => {
		setLoading(true);
		OfficerService.getOfficerGroups(officerId)
			.then(({ data }) => {
				if (callback) callback(data);
			})
			.catch(({ response }) => {
				utils.notify({
					type: "error",
					message: response.data.Message || lang.t("Error getting Officer Groups"),
				});
			})
			.finally(() => setLoading(false));
	};

	/**
	 * remoevs an officer form a specfified group
	 * @param {Number} groupId The unique if of a group
	 */
	const removeGroup = (groupId = favoriteGroup.Id) => {
		_addGroupModal.current.handleOnCancel();
		OrganizationFavoriteGroupsService.deleteGroup(groupId)
			.then(() => {
				utils.notify({
					type: "success",
					message: "Group deleted successfully",
					title: "Success",
				});
				setGroupOptions(prevOptions =>
					prevOptions.filter(option => option.value !== groupId),
				);
				setRawGroups(prevGroups => prevGroups.filter(group => group.Id !== groupId));
			})
			.catch(({ response }) => {
				utils.notify({
					type: "error",
					message: response.data.Message || lang.t("Error getting Officer Groups"),
				});
			});
	};

	/**
	 * Adds a new group to the officer group list
	 * @param {Number} officerId The unique id of the selected officer
	 */
	const addGroup = officerId => {
		_addGroupModal.current.openModal({
			form: {
				name: officerId && typeof officerId !== "object" ? lang.t("My Groups") : "",
				id: undefined,
			},
			onSuccess: () => {
				if (typeof officerId !== "object") {
					// Disabled here because addGroup and addOfficerToGroup both make use of each other resulting in a constant eslint error
					// eslint-disable-next-line no-use-before-define
					getGroups(addOfficerToGroup(officerId));
				} else {
					getGroups();
				}
			},
		});
	};

	/**
	 * Adds an officer to the specified group
	 * @param {Number} officerId The unique id of the selected officer
	 */
	const addOfficerToGroup = officerId => {
		// FE-1422: Get officer to display their name in the modal
		const officer = officers.find(({ id }) => id === officerId);
		_officerGroupsModal.current.openModal({
			officerId,
			addGroup,
			officerGroups: rawGroups,
			officerName: officer.name,
			onSuccess: () => setFavoriteGroup({ ...favoriteGroup }),
		});
	};

	const showRemoveGroupModal = () => {
		if (officers.length > 0) {
			_removeGroupModal.current.openModal({
				groupId: favoriteGroup.Id,
				onSuccess: () => {
					_addGroupModal.current.handleOnCancel();
					setGroupOptions(prevOptions =>
						prevOptions.filter(option => option.value !== favoriteGroup.Id),
					);
					setRawGroups(prevGroups =>
						prevGroups.filter(group => group.Id !== favoriteGroup.Id),
					);
				},
			});
		} else {
			_confirmationModal.current.openModal({
				title: lang.t("Delete Group"),
				message: lang.t("Are you sure you want to delete this group_"),
				confirmButton: `${lang.t("Yes")}, ${lang.t("Delete Group")}`,
				cancelButton: `${lang.t("No")}, ${lang.t("Keep this group_")}`,
				onConfirm: removeGroup,
			});
		}
	};

	const editGroup = () => {
		_addGroupModal.current.openModal({
			form: {
				name: favoriteGroup.Name,
				id: favoriteGroup.Id,
			},
			onRemove: showRemoveGroupModal,
			onSuccess: () => getGroups(),
		});
	};

	const openOfficerGroupsModal = (officerId, isGrouped) => () => {
		// If try to un-group an officer, ask for confirmation
		if (isGrouped) {
			// FE-1422: Get officer to display their name in the modal
			const officer = officers.find(({ id }) => id === officerId);
			getOfficerGroups(officerId, data => {
				_officerGroupsModal.current.openModal({
					officerId,
					officerName: officer.name,
					// PBI-899 & FE-1422: Displays the correct officer groups owned by the internal coord user
					officerGroups: user.IsInternalCoordinator
						? data.filter(item => item.Owned)
						: data,
					isEdit: true,
					onSuccess: () => setFavoriteGroup({ ...favoriteGroup }),
				});
			});
		} else if (rawGroups.length) {
			addOfficerToGroup(officerId);
		} else {
			addGroup(officerId);
		}
	};

	const handleOnFavoriteGroupChange = event => {
		setFavoriteGroup({ Id: event.value, Name: event.label });
		setSearch({ searchQuery: "", activeSearch: false });
	};

	/** Searches for an officer existing in the officers list */
	const searchOfficers = useCallback(() => {
		const searchData = {
			firstName: search.searchQuery,
			lastName: search.searchQuery,
			emailAddress: search.searchQuery,
			mobilePhone: search.searchQuery,
			zipCode: search.searchQuery,
			isAdvancedSearch: false,
		};

		OfficerService.search(
			{
				pageNumber,
				displayCount: queryParams.displayCount,
				vettedStatus: OFFICER_STATUS_TO_FETCH_DATA,
				includeNonJurisdiction: !showMyOfficers,
				includeDeleted: false,
				jobCount: true,
				totalJobsWorked: false,
				upcomingJobsScheduled: false,
			},
			searchData,
		)
			.then(({ data }) => {
				const newData = data?.ItemList ?? [];
				const newOfficers = newData.map(officer => ({
					id: officer.Id,
					image: officer.ContactPhotoUrl,
					name: `${officer.FirstName} ${officer.LastName}`,
					jurisdiction: officer.Department,
					experience: officer.JobCount,
					email: officer.EmailAddress,
					grouped: officer.IsFavorite,
					blocked: officer.DoNotHire,
				}));
				setOfficers(prevState =>
					pageNumber > 1 ? [...prevState, ...newOfficers] : newOfficers,
				);
				setOfficersCount(data.Count);
				setPageNumber(pageNumber + 1);
				setHasMore(newOfficers.length >= queryParams.displayCount);
			})
			.catch(({ response }) => {
				utils.notify({
					type: "error",
					message: response.data.Message || lang.t("Error searching Officers"),
				});
			});
	});

	const changeSearch = ({ target: { value: searchQuery } }) => {
		setSearch({ searchQuery, activeSearch: false });
	};

	const handleSearch = () => {
		setSearch({ ...search, activeSearch: true });
		setFavoriteGroup({ Id: "", Name: "" });
	};

	const handleShowMyOfficersSwitchChange = () => {
		setPageNumber(1);
		setOfficersCount(0);
		setOfficers([]);
		setHasMore(true);
		setShowMyOfficers(prevState => !prevState);
	};

	const onKeyPress = ({ key }) => {
		// Execute search onPress Enter
		if (key === "Enter") handleSearch();
	};

	useEffect(() => {
		getGroups();
	}, []);

	/** Reset visibility of the scrolldetect element after it has become visible once */
	useEffect(() => {
		if (visible) {
			if (search.activeSearch && search.searchQuery !== "") searchOfficers();
			else getGroupedOfficers();
			setVisible(false);
		}
	}, [visible]);

	useEffect(() => {
		if (search.activeSearch && search.searchQuery !== "") searchOfficers();
		else getGroupedOfficers();
	}, [showMyOfficers]);

	useEffect(() => {
		setPageNumber(1);
		setOfficersCount(0);
		setOfficers([]);
		setHasMore(true);
	}, [favoriteGroup]);

	useEffect(() => {
		if (groupOptions.length > 0) {
			const group = [...groupOptions].find(item => item.value === favoriteGroup.Id);
			if (group) setFavoriteGroup({ Id: group.value, Name: group.label });
			else setFavoriteGroup({ Id: "", Name: "" });
		}
	}, [groupOptions]);

	return (
		<div
			id="officer-groups"
			className="container"
		>
			<div className="row groups-page-header">
				<div className="col-sm-6 col-md-8">
					<h1 className="text-center">{lang.t("Officer Groups")}</h1>
				</div>
				<div className="col-sm-3 col-md-2">
					<div className="my-officers-switch">
						<If condition={user.isLEA()}>
							<Switch
								label={lang.t("My Officers Only")}
								onClick={handleShowMyOfficersSwitchChange}
								on={showMyOfficers}
							/>
						</If>
					</div>
				</div>
				<div className="col-sm-3 col-md-2">
					<button
						type="button"
						className="btn btn-primary btn-shadow btn-medium btn-block"
						onClick={addGroup}
					>
						{`+ ${lang.t("New Group")}`}
					</button>
				</div>
			</div>
			<div className="row">
				<div className="col-md-7 officer-search">
					<div className="row">
						<div className="col-xs-12 col-sm-4 col-lg-3 search-label">
							<h4>{lang.t("Search Officers")}</h4>
						</div>
						<div className="col-xs-12 col-sm-8 col-lg-9">
							<div className="input-group">
								<input
									className="form-control"
									type="text"
									value={search.searchQuery}
									onChange={changeSearch}
									onKeyPress={onKeyPress}
								/>
								<span className="input-group-btn">
									<button
										type="button"
										className="btn btn-default"
										onClick={() => handleSearch()}
									>
										<i className="rkf rkf-search" />
									</button>
								</span>
							</div>
						</div>
					</div>
				</div>
				<div className="col-md-5">
					<div className="row">
						<div className="col-xs-12 col-sm-4 search-label">
							<h4>{lang.t("Groups")}</h4>
						</div>
						<div className="col-xs-12 col-sm-8">
							<ReactSelect
								clearable={false}
								name="favoriteGroupId"
								value={search.activeSearch ? "" : favoriteGroup.Id}
								onChange={handleOnFavoriteGroupChange}
								options={groupOptions}
								isLoading={loadingGroups}
							/>
						</div>
					</div>
				</div>
			</div>
			<If condition={favoriteGroup.Id !== ""}>
				<div className="row favorite-group">
					<div className="col-xs-12">
						<h4>{`${favoriteGroup.Name} (${officersCount}) `}</h4>
						<button
							type="button"
							onClick={editGroup}
							className="btn btn-link"
						>
							<i className="rkf rkf-pencil" />
						</button>
					</div>
				</div>
			</If>
			<div className="row header-row hidden-sm hidden-xs">
				<div className="col-md-1" />
				<div className="col-md-2">
					<strong>{lang.t("Name")}</strong>
				</div>
				<div className="col-md-3">
					<strong>{lang.t("Jurisdiction")}</strong>
				</div>
				<div className="col-md-2">
					<strong>{lang.t("Experience")}</strong>
				</div>
				<div className="col-md-3">
					<strong>{lang.t("EMail")}</strong>
				</div>
				<div className="col-md-1" />
			</div>
			<div className="container-fluid table-body">
				<For
					each="officer"
					index="index"
					of={officers}
				>
					<div
						key={`${index}_${officer.id}`}
						className="row v-align-md"
					>
						<div className="col-md-1 hiddem-sm hidden-xs">
							<Link to={`/officers/${officer.id}`}>
								<Image
									src={officer.image}
									fallbackSrc="avatar"
									className="hidden-sm"
								/>
							</Link>
						</div>
						<div className="col-md-2 col-xs-9">
							<Link to={`/officers/${officer.id}`}>{officer.name}</Link>
							<div className="hiddem-sm hidden-xs">
								<strong>#{officer.id}</strong>
							</div>
							<div>{officer.rank}</div>
						</div>
						<div className="col-xs-3 visible-sm-block visible-xs-block text-right">
							<strong>#{officer.id}</strong>
						</div>
						<div className="col-xs-12 col-md-3">{officer.jurisdiction}</div>
						<div className="col-xs-12 col-md-2">
							{`${typeof officer.experience === "number" ? officer.experience : 0} Job${officer.experience !== 1 ? "s" : ""} Worked`}
						</div>
						<div className="col-xs-12 col-md-3 truncate">
							<a
								href={`mailto:${officer.email}`}
								title={officer.email}
							>
								{officer.email}
							</a>
						</div>
						<div className="col-xs-12 col-md-1 text-right">
							<button
								type="button"
								className="btn btn-link btn-icon-only btn-medium"
								onClick={openOfficerGroupsModal(officer.id, officer.grouped)}
							>
								<i
									className={`rkf rkf-shield-star${officer.grouped ? "" : "-o"}`}
								/>
							</button>
						</div>
					</div>
				</For>
				<If condition={hasMore}>
					<ScrollDetect
						className="text-center"
						onVisible={() => setVisible(true)}
					>
						<i className="glyphicon glyphicon-repeat rkf-spin rkf-fw" />
					</ScrollDetect>
				</If>
			</div>
			<ConfirmationModal ref={_confirmationModal} />
			<OfficerGroupsModal
				ref={_officerGroupsModal}
				groups={rawGroups}
				loading={loading}
			/>
			<AddGroupModal ref={_addGroupModal} />
			<RemoveGroupModal
				ref={_removeGroupModal}
				groups={officers}
				rawGroups={rawGroups}
			/>
		</div>
	);
};

OfficerGroups.propTypes = {
	user: PropTypes.object,
};

OfficerGroups.defaultProps = {
	user: {},
};

export default OfficerGroups;
